export const PORTAL_LINK_SCHEME = 'portal://';

const LIST_PATHS: Record<string, string> = {
  'sale.order': '/outbound',
  'product.product': '/warehouse/inventory',
  'purchase.order': '/warehouse/inbound',
  'stock.picking': '/returns',
  'stock.picking.return': '/returns',
  'project.task': '/cases',
  'df.claim': '/cases/claims',
  'df.carrier.company': '/carrier-analytics/carrier-list',
  'df.workflow': '/workflows',
  'df.connector': '/integrations/connectors',
  'df.forecast': '/forecasts',
};

const detailPath = (model: string, id: number): string | null => {
  switch (model) {
    case 'df.claim':
      return `/cases/claims/${id}`;
    case 'df.workflow':
      return `/workflows/${id}`;
    case 'df.connector':
      return `/integrations/connectors/${id}`;
    case 'df.forecast':
      return `/forecasts?open=${id}`;
    case 'stock.picking.return':
      return `/returns/${id}`;
    case 'purchase.order':
    case 'df.carrier.company':
      return LIST_PATHS[model] ? `${LIST_PATHS[model]}?domain=${encodeURIComponent(`[["id","=",${id}]]`)}` : null;
    case 'res.partner':
      return `/contact/${id}`;
    case 'product.product':
      return `/warehouse/inventory/${id}`;
    case 'sale.order':
      return `/outbound/${id}`;
    case 'project.task':
      return `/cases/${id}`;
    case 'stock.picking':
      return `/returns/${id}`;
    default:
      return null;
  }
};

/**
 * Resolve a `portal://` link produced by the AI chat into an in-app route.
 * `portal://<model>/<id>` opens the record, `portal://list/<model>?domain=<odoo domain>`
 * opens the list pre-filtered. Returns null for anything it does not understand.
 */
const IMPORT_PATHS: Record<string, string> = {
  product: '/warehouse/inventory/import',
  outbound: '/outbound/import',
  inbound: '/warehouse/inbound/import/data',
};

export function resolvePortalLink(href: string): string | null {
  if (!href.startsWith(PORTAL_LINK_SCHEME)) return null;
  const rest = href.slice(PORTAL_LINK_SCHEME.length);
  if (rest.startsWith('import/')) return IMPORT_PATHS[rest.slice(7).split(/[?#]/)[0]] ?? null;
  if (rest.startsWith('list/')) {
    const [modelPart, query = ''] = rest.slice(5).split('?');
    const base = LIST_PATHS[modelPart];
    if (!base) return null;
    const params = new URLSearchParams(query);
    const domain = params.get('domain');
    const tab = params.get('tab');
    const out = new URLSearchParams();
    if (domain) out.set('domain', domain);
    if (tab) out.set('tab', tab);
    const qs = out.toString();
    return qs ? `${base}?${qs}` : base;
  }
  const match = rest.match(/^([a-z_.]+)\/(\d+)$/);
  if (!match) return null;
  return detailPath(match[1], Number(match[2]));
}

/** Rewrite every `portal://` link target inside a markdown string into an in-app path. */
export function rewritePortalLinks(markdown: string): string {
  return markdown.replace(/\]\((portal:\/\/[^)\s]+)\)/g, (whole, href: string) => {
    const resolved = resolvePortalLink(href);
    return resolved ? `](${resolved})` : whole;
  });
}
